import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import type { GetAllOversightAssessmentResultsResponse } from '@risksmart-app/trpc/types';
import type { GetAllOversightAssessmentResultsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetAllOversightAssessmentResultsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

/**
 * Maps TRPC oversight assessment result data to match the GraphQL query
 * structure.
 *
 * Each tRPC row carries a single `parents` array; the legacy GraphQL query
 * aliased the same relation twice per row (`oversightAssessments` filtered to
 * the assessment parents, plus an entity-typed alias — `documents` /
 * `obligations` / `risks`). We re-partition `parents` by `ParentType` here so
 * the returned shape matches the GraphQL query. The `__typename` on each row is
 * load-bearing: the register table (`results/config.tsx`, `helpers.ts`,
 * `useLabelledFields.tsx`) dispatches rating/impact/likelihood formatting off
 * it.
 */
export function mapTrpcOversightAssessmentResultsToGraphQL(
  trpcData: GetAllOversightAssessmentResultsResponse
): StrictMappedGraphQLSelection<GetAllOversightAssessmentResultsQuery> {
  return {
    document_oversight_result: trpcData.document_oversight_result.map(
      (row) => ({
        ...row,
        __typename: 'document_oversight_result',
        oversightAssessments: row.parents
          .filter(
            (p) =>
              p.ParentType === ParentTypes.OversightAssessment ||
              p.ParentType === ParentTypes.ComplianceMonitoringAssessment
          )
          .map((p) => ({ oversightAssessment: p.oversightAssessment })),
        documents: row.parents
          .filter((p) => p.ParentType === ParentTypes.Document)
          .map((p) => ({ document: p.document, node: p.node })),
      })
    ),
    obligation_oversight_result: trpcData.obligation_oversight_result.map(
      (row) => ({
        ...row,
        __typename: 'obligation_oversight_result',
        oversightAssessments: row.parents
          .filter(
            (p) =>
              p.ParentType === ParentTypes.OversightAssessment ||
              p.ParentType === ParentTypes.ComplianceMonitoringAssessment
          )
          .map((p) => ({ oversightAssessment: p.oversightAssessment })),
        obligations: row.parents
          .filter((p) => p.ParentType === ParentTypes.Obligation)
          .map((p) => ({ obligation: p.obligation, node: p.node })),
      })
    ),
    risk_controlled_oversight_result:
      trpcData.risk_controlled_oversight_result.map((row) => ({
        ...row,
        __typename: 'risk_controlled_oversight_result',
        oversightAssessments: row.parents
          .filter(
            (p) =>
              p.ParentType === ParentTypes.OversightAssessment ||
              p.ParentType === ParentTypes.ComplianceMonitoringAssessment
          )
          .map((p) => ({ oversightAssessment: p.oversightAssessment })),
        risks: row.parents
          .filter((p) => p.ParentType === ParentTypes.Risk)
          .map((p) => ({ risk: p.risk, node: p.node })),
      })),
    risk_uncontrolled_oversight_result:
      trpcData.risk_uncontrolled_oversight_result.map((row) => ({
        ...row,
        __typename: 'risk_uncontrolled_oversight_result',
        oversightAssessments: row.parents
          .filter(
            (p) =>
              p.ParentType === ParentTypes.OversightAssessment ||
              p.ParentType === ParentTypes.ComplianceMonitoringAssessment
          )
          .map((p) => ({ oversightAssessment: p.oversightAssessment })),
        risks: row.parents
          .filter((p) => p.ParentType === ParentTypes.Risk)
          .map((p) => ({ risk: p.risk, node: p.node })),
      })),
    control_test_oversight_result: trpcData.control_test_oversight_result.map(
      (row) => ({
        ...row,
        __typename: 'control_test_oversight_result',
        linkedControl: row.parent,
        oversightAssessments: row.parents
          .filter(
            (p) =>
              p.ParentType === ParentTypes.OversightAssessment ||
              p.ParentType === ParentTypes.ComplianceMonitoringAssessment
          )
          .map((p) => ({ oversightAssessment: p.oversightAssessment })),
      })
    ),
  };
}

export const useGetAllOversightAssessmentResults = createQueryHook<
  Record<string, never>,
  GetAllOversightAssessmentResultsResponse,
  GetAllOversightAssessmentResultsQuery
>({
  trpcQueryOptions: (trpc) =>
    trpc.frontend.assessment.getAllOversightAssessmentResults.queryOptions(),
  mapTrpcDataToGraphQL: mapTrpcOversightAssessmentResultsToGraphQL,
  graphqlDocument: GetAllOversightAssessmentResultsDocument,
});
